import type { FC } from 'react';
import React, { useState, useRef, useLayoutEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { NoteActionsWrapper, ArchivePageActionLink } from './archivePageBannerNoteSharedStyles.tsx';
import { NoteViewState } from './NoteViewState.ts';
import { ArchivedPageBannerViewNoteActions } from './ArchivedPageBannerViewNoteActions.tsx';

export const i18n = defineMessages({
	addNote: {
		id: 'archive-pages.page-banner.note.actions.add-note',
		defaultMessage: 'Add a note',
		description: 'button text that allows the user to add a descriptive note to the archived page',
	},
});

const COMMENT_LINE_HEIGHT = 20;
const MAX_COLLAPSED_LINES = 3;
const MAX_COLLAPSED_HEIGHT = COMMENT_LINE_HEIGHT * MAX_COLLAPSED_LINES;

// need both -webkit-line-clamp AND height so that we get the trailing ellipsis AND the slide transition on expand.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NoteWrapper = styled.div<{ height?: number }>({
	marginTop: token('space.100'),
	font: token('font.body'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	overflowWrap: 'break-word',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	WebkitLineClamp: (props) => ((props.height || 0) <= MAX_COLLAPSED_HEIGHT ? 3 : 'unset'),
	WebkitBoxOrient: 'vertical',
	display: '-webkit-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => (height ? `${height}px` : 'auto'),
	transition: 'height 0.25s ease-out',
});

type ArchivedPageBannerNoteProps = {
	note: string;
	userCanUpdateNote: boolean;
	openNoteCreation: () => void;
};

export const ArchivedPageBannerViewNote: FC<ArchivedPageBannerNoteProps> = ({
	note,
	userCanUpdateNote,
	openNoteCreation,
}) => {
	const [noteViewState, setNoteViewState] = useState<NoteViewState>(NoteViewState.CONTENT_FITS);

	const noteRef = useRef<HTMLDivElement>(null);
	const totalNoteHeight = useRef<number>();

	useLayoutEffect(() => {
		if (noteRef.current) {
			totalNoteHeight.current = noteRef.current.scrollHeight;
			if (totalNoteHeight.current > MAX_COLLAPSED_HEIGHT) {
				setNoteViewState(NoteViewState.COLLAPSED);
			}
		}
	}, [noteRef, setNoteViewState]);

	if (note) {
		const currentNoteHeight =
			noteViewState === NoteViewState.COLLAPSED ? MAX_COLLAPSED_HEIGHT : totalNoteHeight.current;

		return (
			<div>
				<NoteWrapper ref={noteRef} height={currentNoteHeight} data-testid="view-archive-note">
					{note}
				</NoteWrapper>
				<ArchivedPageBannerViewNoteActions
					noteViewState={noteViewState}
					setNoteViewState={setNoteViewState}
					userCanUpdateNote={userCanUpdateNote}
					openNoteCreation={openNoteCreation}
				/>
			</div>
		);
	}

	if (userCanUpdateNote) {
		return (
			<NoteActionsWrapper>
				<ArchivePageActionLink onClick={openNoteCreation}>
					<FormattedMessage {...i18n.addNote} />
				</ArchivePageActionLink>
			</NoteActionsWrapper>
		);
	}

	return null;
};
